
import { useState } from 'react';
import { useBlogAnalytics } from '@/hooks/blog/useBlogAnalytics';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format, parseISO } from 'date-fns';
import { Loader2, Eye, Feather, Calendar, Tag } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const BlogAnalytics = () => {
  const [timeRange, setTimeRange] = useState<'7days' | '30days' | '90days'>('7days');
  const [activeTab, setActiveTab] = useState('overview');
  const { data, loading, error } = useBlogAnalytics(timeRange);
  const navigate = useNavigate();

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'MMM dd');
    } catch (e) {
      console.error('Error formatting date:', e);
      return dateString;
    }
  };

  const navigateToBlogPost = (slug: string) => {
    window.open(`/blog/${slug}`, '_blank');
  };

  const navigateToEditBlogPost = (id: string) => {
    navigate(`/dashboard/blogs/${id}/edit`);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">Blog Analytics</h1>
          <p className="text-muted-foreground">Track and analyze blog post performance and engagement.</p>
        </div>
        
        <div className="flex items-center space-x-2">
          <Select
            value={timeRange}
            onValueChange={(value) => setTimeRange(value as '7days' | '30days' | '90days')}
          >
            <SelectTrigger className="w-[180px] bg-white dark:bg-gray-800 border-2 text-foreground font-medium">
              <SelectValue placeholder="Select time range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7days">Last 7 Days</SelectItem>
              <SelectItem value="30days">Last 30 Days</SelectItem>
              <SelectItem value="90days">Last 90 Days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="ml-2 text-muted-foreground">Loading analytics data...</p>
        </div>
      ) : error ? (
        <Card className="py-8">
          <CardContent className="pt-6 text-center">
            <p className="text-destructive font-semibold mb-2">Error loading analytics</p>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      ) : data ? (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="posts">Top Posts</TabsTrigger>
            <TabsTrigger value="tags">Tags & Categories</TabsTrigger>
            <TabsTrigger value="trends">Trends</TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Total Views</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{data.totalViews.toLocaleString()}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Across all blog posts
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Published Posts</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{data.totalPosts}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {data.recentPostsCount} new in selected period
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Avg. Views Per Post</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{data.avgViewsPerPost}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Engagement metric
                  </p>
                </CardContent>
              </Card>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Top Posts</CardTitle>
                  <CardDescription>Most viewed blog posts</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {data.topPosts.slice(0, 5).map((post) => (
                      <div key={post.id} className="flex items-center justify-between border-b pb-2 last:border-0">
                        <div className="flex items-start space-x-3">
                          <Eye className="h-4 w-4 text-muted-foreground mt-1 shrink-0" />
                          <div>
                            <button 
                              onClick={() => navigateToBlogPost(post.slug)}
                              className="font-medium text-left hover:text-primary transition-colors line-clamp-1"
                            >
                              {post.title}
                            </button>
                            <p className="text-xs text-muted-foreground">
                              Published: {format(new Date(post.created_at), 'MMM d, yyyy')}
                            </p>
                          </div>
                        </div>
                        <div className="text-sm font-semibold tabular-nums">
                          {(post.view_count || 0).toLocaleString()} views
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Views Over Time</CardTitle>
                  <CardDescription>Blog post views trend</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[250px] w-full">
                    {data.viewsByDateArray.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data.viewsByDateArray.map(item => ({ ...item, date: formatDate(item.date) }))}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="views" stroke="#8884d8" activeDot={{ r: 8 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex justify-center items-center h-full">
                        <p className="text-muted-foreground">No view data available for this period</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="posts">
            <Card>
              <CardHeader>
                <CardTitle>Top Performing Posts</CardTitle>
                <CardDescription>Posts with the highest view counts</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {data.topPosts.map((post) => (
                    <div key={post.id} className="flex items-center justify-between border-b pb-4 last:border-0">
                      <div className="flex items-start space-x-3">
                        <div className="bg-muted rounded-full p-2">
                          <Eye className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <button 
                              onClick={() => navigateToBlogPost(post.slug)}
                              className="font-medium text-left hover:text-primary transition-colors"
                            >
                              {post.title}
                            </button>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            Published: {format(new Date(post.created_at), 'MMMM d, yyyy')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="text-base font-semibold tabular-nums">
                          {(post.view_count || 0).toLocaleString()} views
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => navigateToEditBlogPost(post.id)}
                        >
                          Edit
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="tags">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Top Tags</CardTitle>
                  <CardDescription>Most used tags across blog posts</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px] w-full">
                    {data.tagCounts.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart 
                          data={data.tagCounts.slice(0, 10)}
                          layout="vertical"
                          margin={{ top: 5, right: 30, left: 120, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis 
                            dataKey="name" 
                            type="category" 
                            width={120}
                            tick={{ fontSize: 12 }}
                          />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="count" fill="#8884d8" name="Posts" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex justify-center items-center h-full">
                        <p className="text-muted-foreground">No tag data available</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Content Categories</CardTitle>
                  <CardDescription>Distribution by content category</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px] w-full">
                    {data.categoryCounts.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart 
                          data={data.categoryCounts}
                          layout="vertical"
                          margin={{ top: 5, right: 30, left: 120, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis 
                            dataKey="category" 
                            type="category" 
                            width={120}
                            tick={{ fontSize: 12 }}
                          />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="count" fill="#82ca9d" name="Posts" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex justify-center items-center h-full">
                        <p className="text-muted-foreground">No category data available</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="trends">
            <div className="grid grid-cols-1 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Content Publication Trend</CardTitle>
                  <CardDescription>New posts created over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px] w-full">
                    {data.postCreationByDate.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data.postCreationByDate.map(item => ({ ...item, date: formatDate(item.date) }))}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="posts" stroke="#82ca9d" name="Posts Created" />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex justify-center items-center h-full">
                        <p className="text-muted-foreground">No trend data available for this period</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Recent Posts Performance</CardTitle>
                  <CardDescription>View counts for recently published posts</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {data.recentPosts.length > 0 ? (
                      data.recentPosts.slice(0, 5).map((post) => (
                        <div key={post.id} className="flex items-center justify-between border-b pb-4 last:border-0">
                          <div className="flex items-start space-x-3">
                            <div className="bg-muted rounded-full p-2">
                              <Calendar className="h-5 w-5 text-muted-foreground" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <button 
                                  onClick={() => navigateToBlogPost(post.slug)}
                                  className="font-medium text-left hover:text-primary transition-colors"
                                >
                                  {post.title}
                                </button>
                              </div>
                              <p className="text-sm text-muted-foreground mt-1">
                                Published: {format(new Date(post.created_at), 'MMMM d, yyyy')}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="text-base font-semibold tabular-nums">
                              {(post.view_count || 0).toLocaleString()} views
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <Feather className="h-8 w-8 mx-auto text-muted-foreground opacity-50" />
                        <p className="mt-2 text-muted-foreground">No recent posts in the selected time period</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      ) : (
        <Card className="py-8">
          <CardContent className="pt-6 text-center">
            <p className="text-muted-foreground">No analytics data available</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BlogAnalytics;
